import { JsonValue } from '../serializable';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '../dataset/dataset-trigger-criterion';
import type { InterruptableToolDefinition } from '../chat/types';
import type { WorkflowIdempotencyKey } from './workflow-idempotency-key';
import { PersistedWorkflowTriggerKind } from './workflow';

/**
 * Domain for `workflow_*` Postgres tables (definitions, triggers, runs).
 * Unrelated to the `WF` namespace (`core/workflow`, stack-frame eval graph).
 */
export type WorkflowDefinitionStatus = string;

/**
 * Still `string` because it is a hydration-boundary type fed straight from
 * `workflow_runs.status`. The closed set it should be is
 * `WorkflowRunRollupStatus` in `./workflow-status`; use that (and
 * `isTerminalRunStatus`) for anything that reasons about run state rather than
 * just carrying it.
 */
export type WorkflowRunStatus = string;

export enum WorkflowRunStepStatus {
  Queued = 'queued',
  /**
   * Non-terminal: work has been dispatched and is in flight. Nothing external
   * is being waited on — contrast {@link WorkflowRunStepStatus.Incomplete}.
   */
  Running = 'running',
  /**
   * Non-terminal: the step is *parked* awaiting an external continuation — an
   * interrupted tool that will be completed by a durable wake-up or a client,
   * or a for-each/sub-workflow parent awaiting its children.
   *
   * Only a parked step is safe for a continuation to complete, which is why
   * this is kept distinct from {@link WorkflowRunStepStatus.Running}.
   */
  Incomplete = 'incomplete',
  Completed = 'completed',
  Failed = 'failed',
  Cancelled = 'cancelled',
  /** Terminal: a gate (e.g. filter) evaluated false and intentionally halted this branch. */
  Stopped = 'stopped',
  /**
   * Terminal: a bucket node collected this run's contribution and intentionally
   * ended the branch here. Distinct from {@link WorkflowRunStepStatus.Stopped}
   * (which reads as "pruned by a gate") — an `Absorbed` step's data is retained
   * and surfaces later when the bucket flushes. Like `Stopped`, it does not
   * dispatch successors and counts as a filtered-out branch at a fan-in.
   */
  Absorbed = 'absorbed',
}

export type WorkflowNodeExecutionStatus = WorkflowRunStepStatus;

/**
 * Whether a step instance is parked awaiting an external continuation, and so
 * can be completed from outside the run.
 *
 * This is the invariant that `Running` exists to protect: before it, a
 * `Running` step and a parked one both read as `Incomplete`, so a wake-up could
 * complete a step whose tool had not finished executing.
 */
export const isParkedStepStatus = (
  status: string | null | undefined
): boolean => status === WorkflowRunStepStatus.Incomplete;

export enum WorkflowNodeType {
  Agent = 'agent',
  ForEach = 'for-each',
  AdHocAgent = 'ad-hoc-agent',
  CompleteToolCall = 'complete-tool-call',
  ToolCall = 'tool-call',
  Filter = 'filter',
  RunWorkflow = 'run-workflow',
  EmitEvent = 'emit-event',
  Mapper = 'mapper',
  Debounce = 'debounce',
  Bucket = 'bucket',
  GroupBy = 'group-by',
}

/** Polymorphic trigger discriminator stored on bindings and runs (`trigger_type`). */
export type WorkflowTriggerKind = string;

export type WorkflowData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly status: WorkflowDefinitionStatus;
  /** When set, duplicate runs with the same resolved key are suppressed. */
  readonly idempotencyKey: WorkflowIdempotencyKey | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflow = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly status: WorkflowDefinitionStatus;
  readonly idempotencyKey?: WorkflowIdempotencyKey | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowNodeData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  /** Persisted node type (e.g. `'agent'`). */
  readonly type: WorkflowNodeType;
  readonly data: JsonValue;
  /** Optional ERAQL input type definition for this node. */
  readonly inputShape: string | null;
  /** Optional default output payload emitted by this node. */
  readonly defaultOutput: JsonValue | null;
  /** When set, step input is resolved from this node's output (independent of edges). */
  readonly inputFromNodeId: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowNode = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly type: WorkflowNodeType;
  readonly data: JsonValue;
  readonly inputShape: string | null;
  readonly defaultOutput: JsonValue | null;
  readonly inputFromNodeId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowEdgeData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly condition: unknown;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowEdge = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly condition: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowCronTriggerData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly cron: string;
  readonly timezone: string;
  readonly workflowInput: unknown;
  readonly enabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowCronTrigger = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly cron: string;
  readonly timezone: string;
  readonly workflowInput: unknown;
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowWebhookTriggerData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  /** Stored value (often a hash); treat as sensitive. */
  readonly secret: string;
  readonly providerKey?: string;
  readonly verificationConfig?: unknown;
  readonly eventKey?: string;
  readonly eventConfig?: unknown;
  readonly installStatus?:
    | 'draft'
    | 'installing'
    | 'active'
    | 'error'
    | 'archived';
  readonly enabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowWebhookTrigger = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly secret: string;
  readonly providerKey?: string;
  readonly verificationConfig?: unknown;
  readonly eventKey?: string;
  readonly eventConfig?: unknown;
  readonly installStatus?:
    | 'draft'
    | 'installing'
    | 'active'
    | 'error'
    | 'archived';
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowEventTriggerData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  /** Free-form event name that emit-event nodes publish to. */
  readonly eventName: string;
  readonly enabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowEventTrigger = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly eventName: string;
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowDatasetTriggerData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly datasetId: string;
  readonly criterion: DatasetTriggerCriterion;
  /**
   * Columns whose values decide `record_changed`. Null means every non-internal
   * column, resolved at evaluation time so a new column is watched without
   * editing the trigger.
   */
  readonly trackFields: readonly string[] | null;
  /** Condition tree for `record_first_meets_conditions`; null otherwise. */
  readonly filter: unknown;
  /**
   * Whether activation should fire for rows that already existed. False (the
   * default) primes the ledger instead, so a trigger added to a populated
   * dataset does not storm the run queue.
   */
  readonly processExistingRows: boolean;
  /**
   * Whether the trigger fires on the changes it detects, or only when a user
   * asks it to from the dataset. `manual` still evaluates — that is what keeps
   * the baseline current — it just baselines instead of dispatching.
   */
  readonly runMode: DatasetTriggerRunMode;
  readonly enabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowDatasetTrigger = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly name: string;
  readonly datasetId: string;
  readonly criterion: DatasetTriggerCriterion;
  readonly trackFields: readonly string[] | null;
  readonly filter: unknown;
  readonly processExistingRows: boolean;
  readonly runMode: DatasetTriggerRunMode;
  readonly enabled: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowTriggerBindingData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly triggerType: WorkflowTriggerKind;
  readonly triggerId: string;
  readonly workflowId: string;
  readonly enabled: boolean;
  readonly priority: number;
  readonly defaultInput: unknown;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowTriggerBinding = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly triggerType: WorkflowTriggerKind;
  readonly triggerId: string;
  readonly workflowId: string;
  readonly enabled: boolean;
  readonly priority: number;
  readonly defaultInput: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowResourceGraphNode = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly type: string;
  readonly data?: JsonValue;
  readonly inputShape: string | null;
  readonly defaultOutput?: JsonValue | null;
  readonly inputFromNodeId?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowResourceGraphEdge = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly condition?: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowResourceGraphTriggerBinding = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly triggerType: WorkflowTriggerKind;
  readonly triggerId: string;
  readonly workflowId: string;
  readonly enabled: boolean;
  readonly priority: number;
  readonly defaultInput?: unknown;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowResourceGraphWebhookTrigger = Omit<
  SerializedWorkflowWebhookTrigger,
  'secret'
> & {
  readonly hasSecret?: boolean;
  readonly providerKey?: string;
  readonly verificationConfig?: unknown;
};

export type WorkflowResourceGraphTriggerSnapshot =
  | {
      readonly kind: PersistedWorkflowTriggerKind.Cron;
      readonly trigger: SerializedWorkflowCronTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Webhook;
      readonly trigger: WorkflowResourceGraphWebhookTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Event;
      readonly trigger: SerializedWorkflowEventTrigger;
    }
  | {
      readonly kind: PersistedWorkflowTriggerKind.Dataset;
      readonly trigger: SerializedWorkflowDatasetTrigger;
    };

export enum WorkflowPersistence {
  Ephemeral = 'ephemeral',
  Persisted = 'persisted',
}

export type WorkflowEdgeOutcome = 'success' | 'error';

export type WorkflowEdgeCondition = {
  readonly on: WorkflowEdgeOutcome;
};

export type DraftNodeParams = {
  readonly id?: string;
  readonly type: string;
  readonly data?: JsonValue;
  readonly inputShape?: string | null;
  readonly defaultOutput?: JsonValue | null;
};

export type DraftAgentNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  readonly agentId: string;
  readonly data?: Record<string, unknown>;
};

export type DraftForEachChildNodeParams =
  | ({
      readonly type: WorkflowNodeType.Agent;
    } & DraftAgentNodeParams)
  | ({
      readonly type: WorkflowNodeType.AdHocAgent;
    } & DraftAdHocAgentNodeParams)
  | ({
      readonly type: WorkflowNodeType.CompleteToolCall;
    } & DraftCompleteToolCallNodeParams);

export type DraftForEachNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  readonly itemsPath?: string;
  readonly sourceRunNodeId?: string;
  readonly node?: DraftForEachChildNodeParams;
  readonly data?: Record<string, unknown>;
};

export type DraftAdHocAgentNodeParams = Omit<
  DraftNodeParams,
  'type' | 'data'
> & {
  readonly model: string;
  readonly system?: string;
  readonly toolKeys?: string[];
  readonly mode?: 'auto' | 'manual';
  readonly agent?: string | null;
  readonly agentId?: string | null;
  readonly outputShape?: string;
  readonly conversationId?: string;
  readonly parentConversationId?: string | null;
  readonly parentToolCallId?: string | null;
  readonly data?: Record<string, unknown>;
};

export type DraftCompleteToolCallNodeParams = Omit<
  DraftNodeParams,
  'type' | 'data'
> & {
  readonly parentConversationId: string;
  readonly toolCallId: string;
  readonly parentModel: string;
  readonly mode?: 'auto' | 'manual';
  readonly data?: Record<string, unknown>;
};

export type DraftToolCallNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** Tool key, e.g. "slack/messaging/sendMessage" */
  readonly key: string;
  /** Tool input payload (validated by the tool schema at execution time) */
  readonly params: unknown;
  /** Tool param key -> JSON Pointer into immediate upstream step input */
  readonly paramBindings?: Readonly<Record<string, string>>;
  /**
   * Inline definition for ad-hoc `client/*` interruptable tools (see
   * {@link InterruptableToolDefinition}).
   */
  readonly interruptableTool?: InterruptableToolDefinition;
  readonly data?: Record<string, unknown>;
};

type DraftFilterConditionParams = {
  readonly path?: string;
  readonly operator?: string;
  readonly value?: string;
};

export type DraftFilterNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** How groups combine: 'and' (all) or 'or' (any). Defaults to 'or'. */
  readonly combinator?: 'and' | 'or';
  /** Current shape: groups combined by the top combinator; each group has its own. */
  readonly groups?: readonly {
    readonly combinator?: 'and' | 'or';
    readonly conditions?: readonly DraftFilterConditionParams[];
  }[];
  /** Multi-condition shape: a single AND group (still persisted/read). */
  readonly conditions?: readonly DraftFilterConditionParams[];
  /** Legacy single-condition shape (still persisted/read for older nodes). */
  readonly path?: string;
  readonly operator?: string;
  readonly value?: string;
};

export type DraftRunWorkflowNodeParams = Omit<
  DraftNodeParams,
  'type' | 'data'
> & {
  readonly targetWorkflowId: string;
  /**
   * Pre-generated id for the target workflow run, so dispatchers (e.g. the
   * chat workflow tool) can surface the run to the FE before it completes.
   */
  readonly targetRunId?: string;
  /** Display name of the target workflow (canvas badge). */
  readonly name?: string;
  /**
   * Entry field name → upstream output ref (same format as tool-call
   * `paramBindings` / emit-event `payloadBindings`).
   */
  readonly inputBindings?: Readonly<Record<string, string>>;
};

export type DraftEmitEventNodeParams = Omit<
  DraftNodeParams,
  'type' | 'data'
> & {
  readonly eventName: string;
  /** Payload field name -> upstream output ref. */
  readonly payloadBindings?: Readonly<Record<string, string>>;
};

export type DraftMapperNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** Display name for the canvas card and binding-ref labels. */
  readonly name?: string;
  /**
   * Output field name -> upstream ref, or a string with `{{ref}}` tokens. Same
   * ref format as tool-call `paramBindings` / emit-event `payloadBindings`.
   */
  readonly fieldBindings?: Readonly<Record<string, string>>;
  /** `fieldBindings` keys in authored order (JSONB drops key order). */
  readonly fieldOrder?: readonly string[];
  /** `record` (default) publishes an object; `row` publishes `{ [rowKey]: string[] }`. */
  readonly output?: 'record' | 'row';
  /** The property a `row` mapper publishes its list under; `row` if unset. */
  readonly rowKey?: string;
};

export type DraftDebounceNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** Field identifying what to debounce: `/field` or `Node/field`. */
  readonly keyPath: string;
  /** Window to truncate to. Defaults to 'week'. */
  readonly window?: 'hour' | 'day' | 'week' | 'month';
  /** IANA zone the boundary is computed in. Defaults to 'UTC'. */
  readonly timezone?: string;
  /** Only meaningful for a week window. Defaults to 'monday'. */
  readonly weekStartsOn?: 'monday' | 'sunday';
};

export type DraftBucketNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** Display name for the canvas card and binding-ref labels. */
  readonly name?: string;
  readonly policy: import('./workflow-nodes/bucket-flush-policy').BucketFlushPolicy;
  /** Optional JSON Pointer selecting what to store per contribution. */
  readonly accumulate?: string;
  readonly reducers?: readonly import('./workflow-nodes/bucket-flush-policy').BucketReducer[];
  readonly data?: Record<string, unknown>;
};

export type DraftGroupByNodeParams = Omit<DraftNodeParams, 'type' | 'data'> & {
  /** Display name for the canvas card and binding-ref labels. */
  readonly name?: string;
  /** JSON Pointer to the list being split (blank = the whole input). */
  readonly itemsPath?: string;
  /** JSON Pointer into each item for the value to group on. */
  readonly keyPath: string;
  /**
   * Stable id of the upstream node whose output `itemsPath` reads. Omitted means
   * the direct predecessor; set, the list comes from that ancestor instead —
   * the same source-selection for-each has via its own `sourceRunNodeId`.
   */
  readonly sourceRunNodeId?: string;
  readonly data?: Record<string, unknown>;
};

export type DraftTriggerBindingParams = {
  readonly triggerId?: string;
  readonly bindingId?: string;
  readonly enabled?: boolean;
  readonly priority?: number;
  readonly defaultInput?: unknown;
};

export type DraftCronTriggerParams = DraftTriggerBindingParams & {
  readonly name: string;
  readonly schedule: string;
  readonly timezone: string;
  readonly workflowInput?: unknown;
};

export type DraftWebhookTriggerParams = DraftTriggerBindingParams & {
  readonly name?: string;
  readonly secret: string;
  readonly providerKey?: string;
  readonly verificationConfig?: unknown;
};

export type DraftEventTriggerParams = DraftTriggerBindingParams & {
  readonly name: string;
  /** Free-form event name that emit-event nodes publish to. */
  readonly eventName: string;
};

export type DraftDatasetTriggerParams = DraftTriggerBindingParams & {
  readonly name: string;
  readonly datasetId: string;
  readonly criterion: DatasetTriggerCriterion;
  readonly trackFields?: readonly string[] | null;
  readonly filter?: unknown;
  readonly processExistingRows?: boolean;
  /** Defaults to `automatic`, which is what a trigger normally means. */
  readonly runMode?: DatasetTriggerRunMode;
};

export type DraftEdgeParams = {
  readonly id?: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly condition?: WorkflowEdgeCondition | Record<string, never>;
};

export type WorkflowRunData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly triggerType: WorkflowTriggerKind;
  readonly triggerId: string;
  readonly idempotencyKey: string | null;
  readonly status: WorkflowRunStatus;
  readonly error: string | null;
  readonly startedAt: Date | null;
  readonly finishedAt: Date | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowRun = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly workflowId: string;
  readonly triggerType: WorkflowTriggerKind;
  readonly triggerId: string;
  readonly idempotencyKey: string | null;
  readonly status: WorkflowRunStatus;
  readonly error: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type WorkflowRunNodeExecutionData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly runId: string;
  readonly nodeId: string;
  readonly status: WorkflowNodeExecutionStatus;
  readonly inputId: string | null;
  readonly outputId: string | null;
  readonly error: string | null;
  readonly startedAt: Date | null;
  readonly finishedAt: Date | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

export type SerializedWorkflowRunNodeExecution = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly runId: string;
  readonly nodeId: string;
  readonly status: WorkflowNodeExecutionStatus;
  readonly inputId: string | null;
  readonly outputId: string | null;
  readonly error: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Ordered step within a run (`workflow_run_steps`); payloads in `kv_store`. */
export type WorkflowRunStepData = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly runId: string;
  readonly nodeId: string;
  /** Monotonic order within `runId` (unique with `runId`). */
  readonly stepIndex: number;
  /** `kv_store.id` scoped by `ownerOrgId` */
  readonly inputId: string | null;
  /** `kv_store.id` scoped by `ownerOrgId` */
  readonly outputId: string | null;
  readonly status: WorkflowRunStepStatus;
  readonly createdAt: Date;
  readonly updatedAt: Date;
};

/** Join row between `workflow_run_steps` and `atc_runs`. */
export type WorkflowStepAtcRunData = {
  readonly workflowStepId: string;
  readonly atcRunId: string;
};

export type SerializedWorkflowRunStep = {
  readonly id: string;
  readonly ownerId: string;
  readonly ownerOrgId: string;
  readonly runId: string;
  readonly nodeId: string;
  readonly stepIndex: number;
  readonly inputId: string | null;
  readonly outputId: string | null;
  readonly status: WorkflowRunStepStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type SerializedWorkflowStepAtcRun = {
  readonly workflowStepId: string;
  readonly atcRunId: string;
};
